import React, { useState, useEffect } from "react";
import { Server, Shield, ArrowLeft, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubprocessorList() {
  const [subprocessors, setSubprocessors] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/compliance/subprocessors`)
      .then((r) => r.json())
      .then((data) => {
        setSubprocessors(data.subprocessors || []);
        setLastUpdated(data.last_updated || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#002FA7] mb-8" data-testid="subprocessors-back-link">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="bg-white border-2 border-black p-8 sm:p-12" data-testid="subprocessor-list-page">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
              <Server size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Subprocessor List</h1>
              <p className="text-sm text-gray-500 mt-1">Last updated: {lastUpdated || "Loading..."}</p>
            </div>
          </div>

          <p className="text-sm text-gray-700 leading-relaxed mb-8">
            MOSAIC uses a limited number of third-party service providers ("subprocessors") to operate the platform. Each subprocessor has signed a Data Processing Agreement (DPA) with Lerno Co. ensuring equivalent data privacy and security protections. This list is maintained in compliance with NYCPS ERMA requirements and NYS Education Law &sect; 2-d.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-gray-400" size={24} />
            </div>
          ) : (
            <div className="space-y-6">
              {subprocessors.map((sp, i) => (
                <div key={i} className="border-2 border-gray-200 p-6 hover:border-gray-400 transition-colors" data-testid={`subprocessor-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Shield size={18} className="text-[#002FA7]" />
                    <h3 className="font-bold text-base">{sp.name}</h3>
                    {sp.dpa_signed && (
                      <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                        DPA Signed
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Purpose</p>
                      <p className="text-gray-700">{sp.purpose}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Data Processed</p>
                      <p className="text-gray-700">{sp.data_processed}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Location</p>
                      <p className="text-gray-700">{sp.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-gray-200">
            <h2 className="font-bold text-base mb-3">Notification of Changes</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Lerno Co. will notify contracted schools/districts at least 30 days before adding a new subprocessor. Schools may object to a new subprocessor within 15 days of notification. Contact <a href="mailto:privacy@mosaicassessmentco.com" className="text-[#002FA7] underline">privacy@mosaicassessmentco.com</a> for questions.
            </p>
          </div>

          <div className="mt-6 text-sm text-gray-500">
            <p>Related: <Link to="/privacy" className="text-[#002FA7] underline">Privacy Policy</Link> &middot; <Link to="/terms" className="text-[#002FA7] underline">Terms of Service</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
